/**
 * `state` du Sign in with Apple (flux web redirect) — encodage partagé
 * front ⇄ backend.
 *
 * Apple renvoie ce paramètre tel quel dans le form_post vers
 * `/auth/apple/callback`. C'est le SEUL canal qui survit à l'aller-retour :
 * Apple POSTe depuis ses serveurs, donc le backend ne reçoit ni les headers
 * `X-Alterrego-*` du front, ni l'origine de la page. Sans lui, le callback
 * retombe sur le tenant/env par défaut — la régression que #101 a corrigée.
 *
 * Volontairement du base64url de JSON, pas un JWT signé : le contenu n'est pas
 * un secret et n'est pas une preuve. Le backend NE FAIT PAS confiance à
 * `returnUrl` (cf. `isAllowedReturnUrl` côté backend, qui le valide contre une
 * allowlist d'origines avant toute redirection).
 */

/** Contexte de l'onglet appelant, à restituer au retour d'Apple. */
export interface AppleAuthState {
  /** URL de retour, rejouée par le backend avec le JWT en fragment. */
  returnUrl: string;
  /** Tenant émis par le front (`X-Alterrego-Tenant` sur les autres appels). */
  tenantId: string;
  /** Env émis par le front (`X-Alterrego-Env`). Doit rester cohérent, sinon le
   *  JWT est frappé dans un env que les appels suivants n'utilisent pas. */
  env: string;
}

/**
 * Hôtes autorisés comme cible de retour, cf. `isAllowedReturnUrl`.
 * Alignés sur `BASE_DOMAINS` de `module-branding/resolver.ts`.
 */
const ALLOWED_RETURN_DOMAINS = ['apps.alterrego.com', 'boostore.com'];

/**
 * `returnUrl` est-elle une cible de redirection légitime ?
 *
 * **Garde-fou de sécurité, pas une commodité.** `state` transite par le
 * navigateur de l'utilisateur : n'importe qui peut fabriquer une URL
 * d'autorisation Apple avec le `state` de son choix. Le callback y redirige
 * avec le JWT **dans le fragment** — rediriger sans valider revient à livrer une
 * session valide à l'origine indiquée par l'attaquant (open redirect + vol de
 * jeton). La cible doit donc être un de nos domaines, jamais ce que dit l'appelant.
 *
 * HTTPS obligatoire : en clair, le fragment fuiterait sur le réseau.
 */
export function isAllowedReturnUrl(returnUrl: string): boolean {
  let url: URL;
  try {
    url = new URL(returnUrl);
  } catch {
    return false;
  }
  if (url.protocol !== 'https:') return false;
  // Un `@` ou des identifiants dans l'URL peuvent tromper une lecture humaine ;
  // `URL` résout correctement le host, mais autant refuser ces formes.
  if (url.username || url.password) return false;

  return ALLOWED_RETURN_DOMAINS.some(
    // `endsWith('.' + d)` et pas `includes(d)` : sinon
    // `apps.alterrego.com.evil.tld` passerait.
    (d) => url.hostname === d || url.hostname.endsWith(`.${d}`),
  );
}

/** base64 → base64url (Apple recolle le state dans une URL). */
function toBase64Url(b64: string): string {
  return b64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function fromBase64Url(s: string): string {
  const b64 = s.replace(/-/g, '+').replace(/_/g, '/');
  return b64 + '='.repeat((4 - (b64.length % 4)) % 4);
}

/**
 * Encode le state pour l'URL d'autorisation Apple.
 *
 * `TextEncoder` + `btoa` plutôt que `Buffer` : ce module est importé par le
 * backend (Node) ET par le bundle navigateur, et `Buffer` n'existe pas côté
 * navigateur. Le passage par UTF-8 est nécessaire — `btoa` seul rejette tout
 * caractère hors latin1.
 */
export function encodeAppleState(state: AppleAuthState): string {
  const bytes = new TextEncoder().encode(JSON.stringify(state));
  let binary = '';
  for (const b of bytes) binary += String.fromCharCode(b);
  return toBase64Url(btoa(binary));
}

/**
 * Décode le state renvoyé par Apple.
 *
 * Tolère l'ancien format (state = URL de retour brute) pour ne pas casser un
 * aller-retour déjà en vol au moment du déploiement, et rend `null` sur toute
 * entrée inexploitable — ce paramètre vient du réseau, il ne doit jamais throw.
 */
export function decodeAppleState(raw: string | undefined | null): AppleAuthState | null {
  if (!raw) return null;

  // Ancien format : state = returnUrl brute.
  if (/^https?:\/\//i.test(raw)) {
    return { returnUrl: raw, tenantId: '', env: '' };
  }

  try {
    const binary = atob(fromBase64Url(raw));
    const bytes = Uint8Array.from(binary, (ch) => ch.charCodeAt(0));
    const json = new TextDecoder().decode(bytes);
    const parsed = JSON.parse(json) as Partial<AppleAuthState>;
    if (typeof parsed?.returnUrl !== 'string' || !parsed.returnUrl) return null;
    return {
      returnUrl: parsed.returnUrl,
      tenantId: typeof parsed.tenantId === 'string' ? parsed.tenantId : '',
      env: typeof parsed.env === 'string' ? parsed.env : '',
    };
  } catch {
    return null;
  }
}
